import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';

function Dropdown({ label, items, isOpen, onToggle, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    if (isOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, onClose]);

  return (
    <div ref={ref} className="relative">
      <button onClick={onToggle} className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors inline-flex items-center gap-1 ${isOpen ? 'bg-primary-100 text-primary-700' : 'text-gray-700 hover:bg-gray-100'}`}>
        {label}
        <svg className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
      </button>
      {isOpen && (
        <div className="absolute left-0 mt-1 w-56 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-30 py-1">
          {items.map((item, i) => item.divider ? (
            <div key={i} className="border-t border-gray-100 my-1" />
          ) : (
            <Link key={i} to={item.to} onClick={onClose} className={`block px-4 py-2.5 text-sm transition-colors ${item.active ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-700 hover:bg-gray-50'}`}>
              <div className="font-medium">{item.label}</div>
              {item.desc && <div className="text-xs text-gray-400 mt-0.5">{item.desc}</div>}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  const toggle = (name) => setOpenDropdown(prev => prev === name ? null : name);
  const closeDropdowns = () => setOpenDropdown(null);

  const { isAdmin } = useAuth();

  const analysisItems = [
    { to: '/qa', label: 'Legal Q&A', desc: 'Research legal questions', active: isActive('/qa') || isActive('/legal-qa') },
    { to: '/contract-analysis', label: 'Contract Review', desc: 'Upload & review contracts', active: isActive('/contract-analysis') },
    { to: '/law-analysis', label: 'Law Analysis', desc: 'Research legislation & statutes', active: isActive('/law-analysis') },
    { to: '/policy-analysis', label: 'Policy Analysis', desc: 'Research government policies', active: isActive('/policy-analysis') },
    { to: '/regulation-analysis', label: 'Regulation Analysis', desc: 'City, county, state & agency rules', active: isActive('/regulation-analysis') },
    ...(isAdmin ? [
      { to: '/legal-strategy', label: 'Legal Strategy', desc: 'Internal research tool', active: isActive('/legal-strategy') },
    ] : []),
    { divider: true },
    { to: '/patent-analysis', label: 'Patent Research', desc: 'Search patents & prior art', active: isActive('/patent-analysis') },
    { to: '/compliance', label: 'Compliance Review', desc: 'GDPR, HIPAA, SOX & more', active: isActive('/compliance') },
    ...(isAdmin ? [
      { to: '/legal-services', label: 'Business Filing', desc: 'LLC, DBA & trademark research', active: isActive('/legal-services') },
      { to: '/tax-strategy', label: 'Tax Research', desc: 'Tax obligation information', active: isActive('/tax-strategy') },
    ] : []),
  ];

  const servicesItems = [
    ...(isAdmin ? [
      { to: '/legal-services', label: 'Business Filing', desc: 'LLC, DBA & trademark research', active: isActive('/legal-services') },
    ] : []),
    { to: '/patent-analysis', label: 'Patent Research', desc: 'Search patents & prior art', active: isActive('/patent-analysis') },
    { to: '/compliance', label: 'Compliance Review', desc: 'GDPR, HIPAA, SOX & more', active: isActive('/compliance') },
  ];

  const analysisActive = analysisItems.some(i => i.active);
  const servicesActive = servicesItems.some(i => i.active);

  return (
    <nav className="bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex-shrink-0">
            <Link to="/" className="text-2xl font-bold text-gray-900 hover:text-primary-600 transition-colors flex items-center gap-3">
              <img src="/logo.png" alt="Legalia Logo" className="h-10 w-10 object-contain" />
              <span>Legalia</span>
            </Link>
          </div>

          <div className="flex items-center space-x-2">
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center space-x-1">
              <Dropdown
                label="Analysis"
                items={analysisItems}
                isOpen={openDropdown === 'analysis'}
                onToggle={() => toggle('analysis')}
                onClose={closeDropdowns}
              />
              {user && (
                <Dropdown
                  label="Services"
                  items={servicesItems}
                  isOpen={openDropdown === 'services'}
                  onToggle={() => toggle('services')}
                  onClose={closeDropdowns}
                />
              )}
            </div>

            {/* User Menu */}
            {user ? (
              <div className="relative hidden md:block ml-2">
                <button onClick={() => setShowUserMenu(!showUserMenu)} className="flex items-center space-x-2 text-sm text-gray-700 hover:text-gray-900 focus:outline-none">
                  <div className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center">
                    <span className="text-sm font-medium text-white">{user.email.charAt(0).toUpperCase()}</span>
                  </div>
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>
                {showUserMenu && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowUserMenu(false)} />
                    <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20">
                      <div className="py-1">
                        <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-200">
                          Signed in as<br /><span className="font-medium text-gray-900">{user.email}</span>
                        </div>
                        <a href="/contributions" onClick={() => setShowUserMenu(false)} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Contributions</a>
                        <button onClick={() => { logout(); setShowUserMenu(false); }} className="block w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50">Sign out</button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <button onClick={() => navigate('/login')} className="hidden md:block px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors">Login</button>
            )}

            {/* Mobile Toggle */}
            <button onClick={() => setShowMobileMenu(!showMobileMenu)} className="md:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100" aria-label="Toggle menu">
              {showMobileMenu ? (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              ) : (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {showMobileMenu && (
          <div className="md:hidden border-t border-gray-200 pb-3">
            <div className="px-4 py-2 mt-2">
              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Analysis</div>
            </div>
            {analysisItems.map((item, i) => (
              <Link key={i} to={item.to} onClick={() => setShowMobileMenu(false)} className={`block px-4 py-2.5 text-sm ${item.active ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-700 hover:bg-gray-50'}`}>{item.label}</Link>
            ))}

            {user && (
              <>
                <div className="px-4 py-2 mt-2 border-t border-gray-100">
                  <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Services</div>
                </div>
                {servicesItems.map((item, i) => (
                  <Link key={i} to={item.to} onClick={() => setShowMobileMenu(false)} className={`block px-4 py-2.5 text-sm ${item.active ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-700 hover:bg-gray-50'}`}>{item.label}</Link>
                ))}
              </>
            )}

            <div className="border-t border-gray-100 mt-2 pt-2">
              {user ? (
                <>
                  <div className="px-4 py-2 text-sm text-gray-500">Signed in as <span className="font-medium text-gray-900">{user.email}</span></div>
                  <a href="/contributions" onClick={() => setShowMobileMenu(false)} className="block w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50">Contributions</a>
                  <button onClick={() => { logout(); setShowMobileMenu(false); }} className="block w-full text-left px-4 py-2.5 text-sm text-red-700 hover:bg-red-50">Sign out</button>
                </>
              ) : (
                <button onClick={() => { navigate('/login'); setShowMobileMenu(false); }} className="block mx-4 w-auto px-4 py-2.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg text-center" style={{ width: 'calc(100% - 2rem)' }}>Login</button>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

export default Navigation;
